import React, { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EditorProvider, useEditor } from '../context/EditorContext';
import { useCart, calculateItemPrice } from '../context/CartContext';
import SpreadshirtPreview from '../components/editor/SpreadshirtPreview';
import EditorControls from '../components/editor/EditorControls';
import CheckoutSheet from '../components/checkout/CheckoutSheet';
import OrderConfirmation from '../components/checkout/OrderConfirmation';
import CartSheet from '../components/cart/CartSheet';
import { Button } from '../components/ui/button';
import { ShoppingBag, Sparkles, Plus, ShoppingCart } from 'lucide-react';
import { preloadAllImages, TSHIRT_COLORS } from '../config/productImages';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const TShirtEditorContent = () => {
  const [searchParams] = useSearchParams();
  const { color, size, view, getAllDesigns, resetEditor, hasAnyDesign } = useEditor();
  const { addToCart, getCartItemCount } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isDesigning, setIsDesigning] = useState(false);
  const [exportedDesigns, setExportedDesigns] = useState(null);
  
  const previewRef = useRef(null);
  
  // Preload all product images on mount
  useEffect(() => {
    preloadAllImages();
  }, []);
  
  // Check if returning from Stripe
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const cancelled = searchParams.get('cancelled');
  
  useEffect(() => {
    if (sessionId || cancelled) {
      setShowConfirmation(true);
    }
  }, [sessionId, cancelled]);
  
  const handleReset = () => {
    setShowConfirmation(false);
    setQuantity(1);
    resetEditor();
  };
  
  if (showConfirmation) {
    return <OrderConfirmation onReset={handleReset} />;
  }
  
  // Export designs BEFORE opening checkout (more reliable on mobile)
  const handleOpenCheckout = async () => {
    // Get designs from context to know which views have content
    const allDesigns = getAllDesigns();
    
    if (previewRef.current?.exportAllDesigns) {
      try {
        const designs = previewRef.current.exportAllDesigns();
        setExportedDesigns(designs);
      } catch (err) {
        // Don't block on export error - continue to checkout
        // Backend will handle missing designs gracefully
        logger.error('Design export failed before checkout:', err);
      }
    }
    
    // Always open checkout - even if export fails
    // This improves UX on mobile where canvas export is unreliable
    setCheckoutOpen(true);
  };
  
  // Calculate dynamic price
  const designs = getAllDesigns();
  const unitPrice = calculateItemPrice(designs);
  
  // Add current design to cart
  const handleAddToCart = () => {
    const colorInfo = TSHIRT_COLORS.find(c => c.hex === color) || { name: 'Custom', hex: color };
    const currentDesigns = getAllDesigns();
    const currentUnitPrice = calculateItemPrice(currentDesigns);
    
    // Deep copy designs to avoid reference issues
    const designsCopy = {
      front: [...currentDesigns.front],
      back: [...currentDesigns.back],
      left: [...currentDesigns.left],
      right: [...currentDesigns.right],
    };
    
    addToCart({
      color: color,
      colorName: colorInfo.name,
      size: size,
      designs: designsCopy,
      quantity: quantity,
      unitPrice: currentUnitPrice,
    });
    
    toast.success('Added to cart!', {
      description: `${colorInfo.name} T-Shirt (${size}) x ${quantity}`,
    });
    
    // Reset for next design
    setQuantity(1);
    resetEditor();
  };
  
  const cartItemCount = getCartItemCount();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-100 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <div className="container mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary/20">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <span className="font-heading font-black text-lg sm:text-xl bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                T-Studios
              </span>
              <p className="text-[10px] sm:text-xs text-gray-500 -mt-0.5 hidden sm:block">Design Your Own</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Add to Cart Button - Hidden on mobile, shown in preview area */}
            <Button
              data-testid="add-to-cart-btn"
              onClick={handleAddToCart}
              variant="outline"
              className="hidden sm:flex rounded-xl px-3 sm:px-4 h-10 sm:h-12 font-semibold border-2 hover:border-primary hover:bg-primary/5 transition-all text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              <span className="hidden md:inline">Add to Cart · </span>${(unitPrice * quantity).toFixed(2)}
            </Button>
            
            {/* Cart Button */}
            <CartSheet onCheckout={() => handleOpenCheckout()} />
            
            {/* Buy Now Button */}
            <Button
              data-testid="buy-now-btn"
              onClick={() => handleOpenCheckout()}
              disabled={cartItemCount === 0 && !hasAnyDesign()}
              className="rounded-xl px-3 sm:px-6 h-10 sm:h-12 bg-gradient-to-r from-primary to-indigo-600 hover:from-primary/90 hover:to-indigo-600/90 text-white font-bold shadow-lg shadow-primary/25 transition-all duration-300 hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              <ShoppingBag className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Buy Now</span>
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-8 items-start">
          {/* Product Preview Area */}
          <div className="lg:col-span-7 flex flex-col items-center">
            <SpreadshirtPreview 
              ref={previewRef}
              quantity={quantity}
              setQuantity={setQuantity}
              unitPrice={unitPrice}
              onAddToCart={handleAddToCart}
              isDesigning={isDesigning}
              onStartDesigning={() => setIsDesigning(true)}
            />
          </div>
          
          {/* Controls Area - Desktop */}
          <div className="lg:col-span-5 hidden lg:block">
            <div className="sticky top-24 bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 border border-gray-100">
              <h2 className="font-heading text-2xl font-black mb-2 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Customize Your Tee
              </h2>
              <EditorControls />
            </div>
          </div>
        </div>
      </main>
      
      {/* Mobile Controls - Fixed Bottom - Only show when designing */}
      {isDesigning && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-2xl safe-area-bottom">
          <EditorControls isMobile={true} onClose={() => setIsDesigning(false)} />
        </div>
      )}
      
      {/* Checkout Sheet - Pass previewRef and pre-exported designs */}
      <CheckoutSheet
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        previewRef={previewRef}
        quantity={quantity}
        preExportedDesigns={exportedDesigns}
      />
      
      {/* Bottom padding for mobile controls - only when designing */}
      {isDesigning && <div className="lg:hidden h-[50vh]" />}
    </div>
  );
};

export const TShirtEditor = () => {
  return (
    <EditorProvider>
      <TShirtEditorContent />
    </EditorProvider>
  );
};

export default TShirtEditor;
